import type { MetricValues } from '@revelio/core';
import type { CompositionV2Entity } from '@revelio/data-access';

import type { PrimaryFilterId } from '../../../filters/types/filters';
import { mapPositionsEntitiesToCompositionV3 } from '../../../filters/utils/role-skill-ent-kind';
import { useChart } from '../../Chart/ChartProvider';
import { useTabContentConfig } from '../../PageConfigProvider/PageConfigProvider';
import type { SecondaryDimensionValues } from '../../PageConfigProvider/types/dimension';
import type {
  DimensionSource,
  PositionedItem,
} from '../../PageConfigProvider/types/item';
import type { CompositionMetricValues } from '../../PageConfigProvider/types/metric';
import { usePageState } from '../../PageState/PageStateProvider';
import { isSingleEntityChart } from '../../PageState/utils/is-single-entity-chart';
import type { SupportedSecondaryDimensions } from '../TopNDataProvider';
import type { DataProviderChildren, DataProviderSet } from '../types';
import { CompositionTopCompetitorsDataProvider } from './CompositionTopCompetitorsDataProvider';
import { CompositionRawDataProvider } from './composition-v3/CompositionRawDataProvider';
import { CompositionTopNDataProvider } from './composition-v3/CompositionTopNDataProvider';
import {
  convertCompositionDataV3,
  convertCompositionTopNHeadcountDataV3,
} from './composition-v3/convert-composition-data-v3';
import {
  convertCompositionToSalaryGrowthV3,
  convertCompositionToWagePressureV3,
} from './composition-v3/market-condition-metrics-v3';

type CompositionDataProviderBaseProps = {
  primaryDimension: PrimaryFilterId;
  dimensionSource?: DimensionSource;
  segment?: SecondaryDimensionValues;
  metric:
    | CompositionMetricValues
    | 'wage_pressure'
    | 'wage_pressure_percentile'
    | 'salary_growth';
  isTimeseries?: boolean;
  entitiesOverride?: CompositionV2Entity[];
  getSelectionBreakdowns?: boolean;
  exchangeRate?: number;
  children: DataProviderChildren<DataProviderSet | undefined>;
};

type TopCompanyHeadcountQuery = {
  type: 'TOP_N';
  segment: SupportedSecondaryDimensions;
};

const TOP_N_SECONDARY_DIMENSIONS = new Set<SupportedSecondaryDimensions>([
  'company',
  'geography',
  'role',
]);

const isTopNSecondaryDimension = (
  dimensionSource: DimensionSource
): dimensionSource is SupportedSecondaryDimensions =>
  TOP_N_SECONDARY_DIMENSIONS.has(
    dimensionSource as SupportedSecondaryDimensions
  );
type TopHeadcountQuery = {
  type: 'TOP_HEADCOUNT';
  segment: Exclude<SecondaryDimensionValues, 'company' | 'timeseries'>;
};
type StandardQuery = {
  type: 'STANDARD';
};

type CompositionQuery =
  | TopCompanyHeadcountQuery
  | TopHeadcountQuery
  | StandardQuery;

const getCompositionQueryType = ({
  item,
  dimensionSourceOverride,
  metric,
}: {
  item: PositionedItem;
  dimensionSourceOverride?: DimensionSource;
  metric?: MetricValues;
}): CompositionQuery => {
  if (item.plot?.chartType !== 'bar' && item.plot?.chartType !== 'table') {
    return { type: 'STANDARD' };
  }

  const dimensionSource = dimensionSourceOverride ?? item.plot?.dimensionSource;
  if (dimensionSource && dimensionSource !== 'primary') {
    if (dimensionSource === 'competitors') {
      return { type: 'TOP_HEADCOUNT', segment: 'competitors' };
    }
    if (dimensionSource === 'company') {
      return { type: 'TOP_N', segment: 'company' };
    }
    if (isTopNSecondaryDimension(dimensionSource) && metric !== 'headcount') {
      return { type: 'TOP_N', segment: dimensionSource };
    }
    if (dimensionSource !== 'timeseries' && metric === 'headcount') {
      return { type: 'TOP_HEADCOUNT', segment: dimensionSource };
    }
  }

  return { type: 'STANDARD' };
};

export const CompositionDataProvider = ({
  primaryDimension,
  dimensionSource,
  segment,
  metric,
  isTimeseries,
  entitiesOverride,
  getSelectionBreakdowns,
  exchangeRate,
  children,
}: CompositionDataProviderBaseProps) => {
  const { item } = useChart();
  const isSingleEntity = isSingleEntityChart(item);
  const { pageState } = usePageState();
  const dimensionSourceOverride =
    dimensionSource ?? pageState.dimensionSourceOverrides?.[item.id];
  const queryType = getCompositionQueryType({
    item,
    dimensionSourceOverride,
    metric,
  });
  const { revelioPageId } = useTabContentConfig();

  const entitiesV3 = mapPositionsEntitiesToCompositionV3(entitiesOverride);

  if (metric === 'salary_growth') {
    return (
      <CompositionRawDataProvider
        primaryDimension={primaryDimension}
        entitiesOverride={entitiesV3}
        isTimeseries={true}
      >
        {({ data, status, error, monthRefs = [] }) =>
          children({
            data: data?.composition_v3
              ? convertCompositionToSalaryGrowthV3({
                  data: data.composition_v3,
                  chartType: item.plot?.chartType ?? 'line',
                  monthRefs,
                })
              : undefined,
            status,
            error,
          })
        }
      </CompositionRawDataProvider>
    );
  }

  if (metric === 'wage_pressure' || metric === 'wage_pressure_percentile') {
    return (
      <CompositionRawDataProvider
        primaryDimension={primaryDimension}
        entitiesOverride={entitiesV3}
        isTimeseries={true}
      >
        {({ data, status, error, monthRefs = [] }) =>
          children({
            data: data?.composition_v3
              ? convertCompositionToWagePressureV3({
                  data: data.composition_v3,
                  monthRefs,
                  variant:
                    metric === 'wage_pressure_percentile'
                      ? 'percentile'
                      : 'value',
                })
              : undefined,
            status,
            error,
          })
        }
      </CompositionRawDataProvider>
    );
  }

  if (queryType.type === 'TOP_N') {
    return (
      <CompositionTopNDataProvider
        primaryDimension={primaryDimension}
        secondaryDimension={queryType.segment}
        metric={metric}
        isTimeseries={isTimeseries}
        exchangeRate={exchangeRate}
      >
        {children}
      </CompositionTopNDataProvider>
    );
  }

  if (queryType.type === 'TOP_HEADCOUNT') {
    const headcountSegment = queryType.segment;
    if (headcountSegment === 'competitors') {
      return (
        <CompositionTopCompetitorsDataProvider
          primaryDimension={primaryDimension}
          metric={metric}
          exchangeRate={exchangeRate}
        >
          {children}
        </CompositionTopCompetitorsDataProvider>
      );
    }

    return (
      <CompositionRawDataProvider
        primaryDimension={primaryDimension}
        getSelectionBreakdowns={!revelioPageId}
      >
        {({ data, status, error }) =>
          children({
            data: data?.composition_v3
              ? convertCompositionTopNHeadcountDataV3({
                  segment: headcountSegment,
                  isGrouped:
                    !isSingleEntity &&
                    item.plot?.chartType !== 'bar' &&
                    item.plot?.chartType !== 'table',
                  data: data.composition_v3,
                })
              : undefined,
            status,
            error,
          })
        }
      </CompositionRawDataProvider>
    );
  }

  return (
    <CompositionRawDataProvider
      primaryDimension={primaryDimension}
      entitiesOverride={entitiesV3}
      getSelectionBreakdowns={getSelectionBreakdowns}
      isTimeseries={isTimeseries}
    >
      {({ data, status, error, monthRefs = [] }) =>
        children({
          data: data?.composition_v3
            ? convertCompositionDataV3({
                metric,
                segment:
                  isTimeseries && !item?.plot?.secondaryDimension
                    ? 'timeseries'
                    : item?.plot?.secondaryDimension || segment,
                data: data.composition_v3,
                isTimeseries,
                exchangeRate,
                monthRefs,
              })
            : undefined,
          status,
          error,
        })
      }
    </CompositionRawDataProvider>
  );
};
